// Battle-page animation hook (Framer Motion driver). Owns:
//   - the BoardRefs registry (the boardRefs anchors that pages /
//     rects rely on);
//   - the per-frame animation orchestrator (`runFrame`);
//   - the play-hover clone lifecycle (`playClone` — used both for
//     the viewer's own play hover and the opponent's incoming play
//     reveal);
//   - the spell-fizzle flag (the clone's cyan dissolve);
//   - the trap-flip flag (a rotateY beat on the field tile);
//   - the bounce-to-opp-hand anchors (motion.divs at the opp-hand
//     anchor that briefly hold a card-<eid> layoutId so Motion has
//     somewhere to FLY the leaving card to);
//   - the combat-lunge overlay state (motion.divs rendered via
//     portal at fixed rects so they bleed past Field's overflow).
//
// Card-to-card zone moves (hand → battle, draw, bounce-to-own-hand,
// dies-to-graveyard, exile) are handled by Motion's `layoutId` on
// the wrapping motion.div at each render site. There is no
// imperative driver beat for those; we just dispatch state and let
// Motion FLIP. The orchestration here only exists for the special
// cases that need pre-/post-dispatch beats.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  CardView,
  GameView,
  PlayerId,
} from "../api/types";
import type { FeedEntry } from "./feed";
import { effectiveDurationMs } from "./animSpeed";
import { cardFxKey, createBoardRefs, type BoardRefs } from "./boardRefs";
import { type CardOverrides } from "./cardOverrides";
import { playSfx } from "./sfx";
import { DURATIONS } from "./motion";

const PLAY_HOVER_MS = DURATIONS.playHover;
const TRAP_HOVER_MS = DURATIONS.trapHover;
const SPELL_HOVER_MS = DURATIONS.spellHover;
const SPELL_FIZZLE_MS = DURATIONS.spellFizzle;
const TRAP_FLIP_MS = DURATIONS.trapFlipHalf * 2;
const ZONE_MOVE_MS = DURATIONS.zoneMove;
const LUNGE_PEAK_OFFSET_MS = 220;

export interface PlayClone {
  eid: number;
  card: CardView;
  rect: DOMRect;
  /** Set once the dissolve has started so the JSX mounts the fizzle
   *  variant on the clone's motion.div. */
  fizzling: boolean;
}

export interface LungeItem {
  eid: number;
  rect: DOMRect;
  card: CardView;
  direction: "up" | "down";
}

/** A short-lived 1px motion.div pinned at the opp-hand anchor, holding
 *  `card-<eid>` so Motion has somewhere to FLY a leaving card to when
 *  it returns to the opponent's hidden hand. The anchor is mounted
 *  the same frame the move dispatches and unmounted when the layout
 *  animation completes. */
export interface BounceAnchor {
  eid: number;
}

export interface BattleAnimationsApi {
  boardRefs: BoardRefs;
  overridesValue: CardOverrides;
  /** Run one frame (a contiguous batch of wire events) and orchestrate
   *  any required pre- / post-dispatch beats (opp-play hover, spell
   *  fizzle, trap flip, bounce). `commit` is called once, synchronously,
   *  at the moment the reducer state should advance. */
  runFrame: (
    prev: GameView | null,
    next: GameView,
    feedEntries: FeedEntry[],
    commit: () => void,
    viewer: PlayerId,
  ) => Promise<void>;
  /** No-transition path: still clears any non-owned hover clone so
   *  intermediate phase ticks don't leave it stranded. */
  applyInstant: (dispatch: () => void) => void;
  setPreviewOverrides: (patch: ReadonlyMap<number, Partial<CardView>>) => void;

  // Play-hover clone lifecycle.
  playClone: PlayClone | null;
  castingDissolvedEid: number | null;
  beginPlayHover: (input: {
    eid: number;
    card: CardView;
    rect: DOMRect;
    hoverMs?: number;
  }) => Promise<void>;
  abortPlayHover: () => void;
  markCastingDissolved: (eid: number) => void;
  awaitSpellFizzle: () => Promise<void>;
  finishPlayHover: () => void;

  /** Fire the lunge keyframes on each attacker eid and thud SFX.
   *  Resolves once every clone's animation has completed cleanup. */
  playCombatLunge: (input: {
    snapshot: GameView;
    viewer: PlayerId;
    attackerEids: number[];
  }) => Promise<void>;

  /** Overlay state read by BattlePage for portal-mounted FX surfaces. */
  lungeItems: ReadonlyArray<LungeItem>;
  bounceAnchors: ReadonlyArray<BounceAnchor>;
  /** Mark a lunge clone as finished — BattlePage's lunge overlay calls
   *  this from each motion.div's onAnimationComplete. */
  completeLunge: (eid: number) => void;
  /** Drop a bounce anchor after its layout animation has resolved. */
  completeBounceAnchor: (eid: number) => void;
}

const EMPTY_SNAP_OVERRIDES: ReadonlyMap<number, Partial<CardView>> = new Map();
const EMPTY_SUPPRESS: ReadonlySet<number> = new Set();
const EMPTY_FLIP: ReadonlySet<number> = new Set();

export interface BattleAnimationsInput {
  snapshot: GameView | null;
  viewer: PlayerId;
}

export function useBattleAnimations(
  input: BattleAnimationsInput,
): BattleAnimationsApi {
  const { snapshot, viewer } = input;

  const boardRefsRef = useRef<BoardRefs | null>(null);
  if (boardRefsRef.current == null) boardRefsRef.current = createBoardRefs();
  const boardRefs = boardRefsRef.current;

  const [snapshotOverrides, setSnapshotOverrides] =
    useState<ReadonlyMap<number, Partial<CardView>>>(EMPTY_SNAP_OVERRIDES);
  const [previewOverrides, setPreviewOverridesState] =
    useState<ReadonlyMap<number, Partial<CardView>>>(EMPTY_SNAP_OVERRIDES);
  const [flippingEids, setFlippingEids] =
    useState<ReadonlySet<number>>(EMPTY_FLIP);

  // --- Play-hover clone state --------------------------------------
  const [playClone, setPlayCloneState] = useState<PlayClone | null>(null);
  const playCloneRef = useRef<PlayClone | null>(null);
  const setPlayClone = useCallback((next: PlayClone | null) => {
    playCloneRef.current = next;
    setPlayCloneState(next);
  }, []);
  // True between a spell hover-end and the submitAction finally. While
  // set, the dispatch wrapper and the instant-path both must NOT clear
  // the clone — submitAction owns it through the dissolve.
  const spellOwnedRef = useRef(false);
  const [castingDissolvedEid, setCastingDissolvedEid] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (castingDissolvedEid == null || !snapshot) return;
    const stillInHand = snapshot.players[viewer].hand?.some(
      (c) => c.entity_id === castingDissolvedEid,
    );
    if (!stillInHand) setCastingDissolvedEid(null);
  }, [snapshot, castingDissolvedEid, viewer]);

  // While the play clone is up, the hand slot must drop its layoutId
  // or Motion would see two elements with `card-<eid>` and pair the
  // wrong one.
  const suppressedEids = useMemo<ReadonlySet<number>>(
    () => (playClone ? new Set([playClone.eid]) : EMPTY_SUPPRESS),
    [playClone],
  );

  const mergedSnapshotOverrides = useMemo<
    ReadonlyMap<number, Partial<CardView>>
  >(() => {
    if (snapshotOverrides.size === 0) return previewOverrides;
    if (previewOverrides.size === 0) return snapshotOverrides;
    const out = new Map<number, Partial<CardView>>();
    for (const [eid, patch] of previewOverrides) out.set(eid, patch);
    for (const [eid, patch] of snapshotOverrides) {
      out.set(eid, { ...out.get(eid), ...patch });
    }
    return out;
  }, [snapshotOverrides, previewOverrides]);

  const overridesValue = useMemo<CardOverrides>(
    () => ({
      snapshot: mergedSnapshotOverrides,
      suppress: suppressedEids,
      flipping: flippingEids,
    }),
    [mergedSnapshotOverrides, suppressedEids, flippingEids],
  );

  // --- Lunge / bounce-anchor overlays ------------------------------
  const [lungeItems, setLungeItems] = useState<ReadonlyArray<LungeItem>>([]);
  const lungeResolversRef = useRef<Map<number, () => void>>(new Map());
  const [bounceAnchors, setBounceAnchors] = useState<ReadonlyArray<BounceAnchor>>(
    [],
  );

  const completeLunge = useCallback((eid: number) => {
    setLungeItems((prev) => prev.filter((it) => it.eid !== eid));
    const resolver = lungeResolversRef.current.get(eid);
    if (resolver) {
      lungeResolversRef.current.delete(eid);
      resolver();
    }
    // Unhide the original FX wrapper.
    const el = boardRefs.get(cardFxKey(eid));
    if (el) el.style.visibility = "";
  }, [boardRefs]);

  const completeBounceAnchor = useCallback((eid: number) => {
    setBounceAnchors((prev) => prev.filter((a) => a.eid !== eid));
  }, []);

  // --- Frame orchestration -----------------------------------------
  const clearCloneOnDispatch = useCallback(() => {
    if (spellOwnedRef.current) return;
    if (playCloneRef.current?.fizzling) return;
    if (playCloneRef.current) setPlayClone(null);
  }, [setPlayClone]);

  const runFrame = useCallback(
    async (
      prev: GameView | null,
      next: GameView,
      feedEntries: FeedEntry[],
      commit: () => void,
      viewerSeat: PlayerId,
    ): Promise<void> => {
      const flow = detectFlow(prev, next, feedEntries, viewerSeat);

      // Trap flip: before the move, override face_down=false and run
      // the rotateY beat on the field tile. The field tile reads
      // overrides.flipping to decide whether to play the variant.
      if (flow.trapFlipEids.size > 0) {
        const overrides = new Map<number, Partial<CardView>>();
        for (const eid of flow.trapFlipEids) {
          overrides.set(eid, { face_down: false });
        }
        // Show the spring's victims taking damage AS the trap springs,
        // not when the post-flip commit lands. See springVictimOverrides.
        if (prev) {
          for (const [eid, patch] of springVictimOverrides(prev, next)) {
            overrides.set(eid, patch);
          }
        }
        setSnapshotOverrides(overrides);
        setFlippingEids(flow.trapFlipEids);
        await pacedSleep(TRAP_FLIP_MS);
        setFlippingEids(EMPTY_FLIP);
      }

      // Bounce-to-opp-hand: mount a 1px motion.div at the opp anchor
      // carrying card-<eid> so Motion has a destination rect to FLY
      // the leaving field tile to. We mount it BEFORE dispatch so the
      // pairing is in place the moment the field tile unmounts.
      if (flow.bounceToOppHandEid != null) {
        setBounceAnchors((prev) => [
          ...prev,
          { eid: flow.bounceToOppHandEid! },
        ]);
      }

      // Trap set (hand → battle, face-down): the viewer just saw the
      // card's face in hand, so override face_down=false BEFORE the
      // dispatch — the arriving field tile then renders face-up
      // through the zone move and flips down afterwards (below).
      if (flow.trapSetEids.size > 0) {
        const overrides = new Map<number, Partial<CardView>>();
        for (const eid of flow.trapSetEids) {
          overrides.set(eid, { face_down: false });
        }
        setSnapshotOverrides(overrides);
      }

      clearCloneOnDispatch();
      commit();
      // Clear any unflip face_down override now that the move has
      // dispatched and the entity row reflects the engine's reality.
      // (Set overrides survive — the set flip below owns them.)
      if (flow.trapFlipEids.size > 0 && flow.trapSetEids.size === 0) {
        setSnapshotOverrides(EMPTY_SNAP_OVERRIDES);
      }

      // After dispatch, give Motion a beat to FLIP the layout
      // transitions. The chunker's MIN_STEP_DELAY_MS pacing layered
      // on top by BattlePage handles inter-chunk spacing; here we
      // just await the in-frame motion duration.
      await pacedSleep(ZONE_MOVE_MS);

      // Trap set, leg two: the tile has landed face-up; run the
      // rotateY beat and drop the override at the midpoint, where the
      // variant's opacity dip hides the face → card-back swap.
      if (flow.trapSetEids.size > 0) {
        setFlippingEids(flow.trapSetEids);
        await pacedSleep(DURATIONS.trapFlipHalf);
        setSnapshotOverrides(EMPTY_SNAP_OVERRIDES);
        await pacedSleep(DURATIONS.trapFlipHalf);
        setFlippingEids(EMPTY_FLIP);
      }

      // Bounce anchor cleanup happens when its layout animation
      // resolves (via Motion's onLayoutAnimationComplete). Also clear
      // it after the wait above as a safety in case the anchor never
      // mounted (e.g. opp anchor missing).
      if (flow.bounceToOppHandEid != null) {
        completeBounceAnchor(flow.bounceToOppHandEid);
      }
    },
    [clearCloneOnDispatch, completeBounceAnchor],
  );

  const applyInstant = useCallback(
    (dispatch: () => void) => {
      clearCloneOnDispatch();
      dispatch();
    },
    [clearCloneOnDispatch],
  );

  const setPreviewOverrides = useCallback(
    (patch: ReadonlyMap<number, Partial<CardView>>) =>
      setPreviewOverridesState(
        patch.size === 0 ? EMPTY_SNAP_OVERRIDES : patch,
      ),
    [],
  );

  // --- Play-hover lifecycle ----------------------------------------
  const beginPlayHover = useCallback(
    async ({
      eid,
      card,
      rect,
      hoverMs,
    }: {
      eid: number;
      card: CardView;
      rect: DOMRect;
      /** Override the per-type hover beat (the dwell BEFORE the cast
       *  animation). The opp-play path passes a longer value so an
       *  incoming card the viewer didn't choose is readable before it
       *  fizzles / lands. */
      hoverMs?: number;
    }) => {
      setPlayClone({ eid, card, rect, fizzling: false });
      const dwell = (base: number) =>
        new Promise<void>((r) =>
          window.setTimeout(r, effectiveDurationMs(hoverMs ?? base)),
        );
      if (card.type === "spell") {
        spellOwnedRef.current = true;
        await dwell(SPELL_HOVER_MS);
        if (playCloneRef.current) {
          setPlayClone({ ...playCloneRef.current, fizzling: true });
        }
      } else if (card.type === "trap") {
        await dwell(TRAP_HOVER_MS);
      } else {
        await dwell(PLAY_HOVER_MS);
      }
    },
    [setPlayClone],
  );

  const abortPlayHover = useCallback(() => {
    setPlayClone(null);
    spellOwnedRef.current = false;
  }, [setPlayClone]);

  const markCastingDissolved = useCallback((eid: number) => {
    setCastingDissolvedEid(eid);
  }, []);

  const awaitSpellFizzle = useCallback(async () => {
    if (!spellOwnedRef.current) return;
    await new Promise<void>((r) =>
      window.setTimeout(r, effectiveDurationMs(SPELL_FIZZLE_MS)),
    );
  }, []);

  const finishPlayHover = useCallback(() => {
    if (!spellOwnedRef.current) return;
    spellOwnedRef.current = false;
    if (playCloneRef.current) setPlayClone(null);
  }, [setPlayClone]);

  // --- Combat lunge ------------------------------------------------
  const playCombatLunge = useCallback(
    ({
      snapshot: snap,
      viewer: viewerSeat,
      attackerEids,
    }: {
      snapshot: GameView;
      viewer: PlayerId;
      attackerEids: number[];
    }): Promise<void> => {
      if (attackerEids.length === 0) return Promise.resolve();
      const attackerOnViewerSide = snap.players[viewerSeat].battle.some((b) =>
        attackerEids.includes(b.entity_id),
      );
      const direction: "up" | "down" = attackerOnViewerSide ? "up" : "down";

      const items: LungeItem[] = [];
      const completions: Array<Promise<void>> = [];
      for (const eid of attackerEids) {
        const el = boardRefs.get(cardFxKey(eid));
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) continue;
        // Find the attacker's CardView in the snapshot.
        let card: CardView | undefined;
        for (const seat of snap.players) {
          card = seat.battle.find((c) => c.entity_id === eid);
          if (card) break;
        }
        if (!card) continue;
        items.push({ eid, rect, card, direction });
        // Hide the original so the user only sees the floating clone.
        el.style.visibility = "hidden";
        completions.push(
          new Promise<void>((resolve) => {
            lungeResolversRef.current.set(eid, resolve);
            // Safety: if the motion clone's onAnimationComplete never
            // fires (the element is unmounted mid-flight by an
            // unrelated dispatch), unblock after the scaled duration
            // plus a generous margin.
            window.setTimeout(() => {
              completeLunge(eid);
            }, effectiveDurationMs(DURATIONS.combatLunge) + 200);
          }),
        );
      }
      if (items.length === 0) return Promise.resolve();
      setLungeItems((prev) => [...prev, ...items]);
      window.setTimeout(
        () => playSfx("ai_attack_thud"),
        effectiveDurationMs(LUNGE_PEAK_OFFSET_MS),
      );
      return Promise.all(completions).then(() => undefined);
    },
    [boardRefs, completeLunge],
  );

  return useMemo(
    () => ({
      boardRefs,
      overridesValue,
      runFrame,
      applyInstant,
      setPreviewOverrides,
      playClone,
      castingDissolvedEid,
      beginPlayHover,
      abortPlayHover,
      markCastingDissolved,
      awaitSpellFizzle,
      finishPlayHover,
      playCombatLunge,
      lungeItems,
      bounceAnchors,
      completeLunge,
      completeBounceAnchor,
    }),
    [
      boardRefs,
      overridesValue,
      runFrame,
      applyInstant,
      setPreviewOverrides,
      playClone,
      castingDissolvedEid,
      beginPlayHover,
      abortPlayHover,
      markCastingDissolved,
      awaitSpellFizzle,
      finishPlayHover,
      playCombatLunge,
      lungeItems,
      bounceAnchors,
      completeLunge,
      completeBounceAnchor,
    ],
  );
}

// ---------------------------------------------------------------------
// Frame detection
// ---------------------------------------------------------------------

interface DetectedFlow {
  /** Trap-flip eids — face-down traps leaving the battlefield or being
   *  flipped face-up in place. */
  trapFlipEids: ReadonlySet<number>;
  /** Trap-set eids — traps played from a visible hand that arrive on
   *  the battlefield face-down. The viewer just saw the card's face,
   *  so the tile flies in face-up and flips down after the zone move.
   *  (Opponent sets never match: their hand is hidden, so the card
   *  has no prev-frame hand entry to detect from.) */
  trapSetEids: ReadonlySet<number>;
  /** Opp eid that left opp battle this frame with no visible destination
   *  — animate as a bounce to the opp-hand anchor. */
  bounceToOppHandEid: number | null;
}

function indexZones(
  snap: GameView,
): Map<number, { card: CardView; zone: string; seat: number }> {
  const out = new Map<number, { card: CardView; zone: string; seat: number }>();
  const add = (
    cards: CardView[] | null | undefined,
    zone: string,
    seat: number,
  ) => {
    if (!cards) return;
    for (const c of cards) out.set(c.entity_id, { card: c, zone, seat });
  };
  for (const seat of [0, 1]) {
    const s = snap.players[seat];
    add(s.hand, "hand", seat);
    add(s.battle, "battle", seat);
    add(s.graveyard, "graveyard", seat);
    add(s.exile, "exile", seat);
    add(s.deck, "deck", seat);
  }
  return out;
}

function detectFlow(
  prev: GameView | null,
  next: GameView,
  feedEntries: FeedEntry[],
  viewer: number,
): DetectedFlow {
  const out: DetectedFlow = {
    trapFlipEids: new Set(),
    trapSetEids: new Set(),
    bounceToOppHandEid: null,
  };
  if (!prev) return out;
  const pIdx = indexZones(prev);
  const nIdx = indexZones(next);
  const trapFlipEids = new Set<number>();
  const trapSetEids = new Set<number>();
  for (const [eid, before] of pIdx) {
    const after = nIdx.get(eid);
    if (
      before.card.type === "trap" &&
      before.zone === "battle" &&
      before.card.face_down === true &&
      (after?.zone !== "battle" || after.card.face_down === false)
    ) {
      trapFlipEids.add(eid);
    }
    if (
      before.card.type === "trap" &&
      before.zone === "hand" &&
      after?.zone === "battle" &&
      after.card.face_down === true
    ) {
      trapSetEids.add(eid);
    }
  }
  if (trapFlipEids.size > 0) out.trapFlipEids = trapFlipEids;
  if (trapSetEids.size > 0) out.trapSetEids = trapSetEids;
  const opp = (1 - viewer) as 0 | 1;
  for (const entry of feedEntries) {
    if (entry.kind !== "bounced") continue;
    const firstCard = entry.parts.find((p) => p.type === "card");
    if (!firstCard || firstCard.type !== "card") continue;
    const eid = firstCard.eid;
    if (eid == null) continue;
    const before = pIdx.get(eid);
    if (!before) continue;
    if (before.seat !== opp || before.zone !== "battle") continue;
    if (nIdx.has(eid)) continue;
    out.bounceToOppHandEid = eid;
    break;
  }
  return out;
}

/** Overrides that preview a trap spring's damage on its victims so the
 *  life loss reads the instant the trap springs — not when the flip's
 *  commit lands ~640 ms later. A spring (e.g. King's Portcullis pinging
 *  each attacker) rides in the SAME chunk as the trap's flip; `commit()`
 *  only runs after the flip's await, so without this preview the damaged
 *  creatures keep showing full life all the way through the flip.
 *  Survivors show their post-spring `remaining_life`; creatures pinged to
 *  death show drained in place before the commit flies them to the
 *  graveyard. Pure: derived from the prev/next views. */
export function springVictimOverrides(
  prev: GameView,
  next: GameView,
): Map<number, Partial<CardView>> {
  const out = new Map<number, Partial<CardView>>();
  const before = indexZones(prev);
  const after = indexZones(next);
  for (const [eid, b] of before) {
    if (b.zone !== "battle" || b.card.type !== "creature") continue;
    const a = after.get(eid);
    if (a?.zone === "battle") {
      if (a.card.remaining_life < b.card.remaining_life) {
        out.set(eid, {
          damage_marked: a.card.damage_marked,
          remaining_life: a.card.remaining_life,
        });
      }
    } else if (a?.zone === "graveyard") {
      out.set(eid, { damage_marked: b.card.current_life, remaining_life: 0 });
    }
  }
  return out;
}

async function pacedSleep(baseMs: number): Promise<void> {
  return new Promise((r) => window.setTimeout(r, effectiveDurationMs(baseMs)));
}
